import { AlertTriangle, CheckCircle2, HelpCircle, Paperclip, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { LineItemMatchStatus } from '../types/quotation.types';

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning';

interface StatusConfig {
  label: string;
  variant: BadgeVariant;
  /** Tailwind text color for the compact (icon-only) rendering. */
  iconColor: string;
  icon: React.ComponentType<{ className?: string }>;
}

const config: Record<LineItemMatchStatus, StatusConfig> = {
  MATCHED: {
    label: 'Matched',
    variant: 'success',
    iconColor: 'text-success',
    icon: CheckCircle2,
  },
  NEEDS_CLARIFICATION: {
    label: 'Needs info',
    variant: 'warning',
    iconColor: 'text-warning',
    icon: AlertTriangle,
  },
  NO_MATCH: {
    label: 'No match',
    variant: 'destructive',
    iconColor: 'text-destructive',
    icon: XCircle,
  },
  SUMMARY_ONLY: {
    label: 'Summary',
    variant: 'outline',
    iconColor: 'text-muted-foreground',
    icon: HelpCircle,
  },
  EXTERNAL_ATTACHMENT: {
    label: 'Attachment',
    variant: 'outline',
    iconColor: 'text-muted-foreground',
    icon: Paperclip,
  },
};

interface LineItemStatusBadgeProps {
  status: LineItemMatchStatus;
  /** Icon-only rendering for tight surfaces (e.g. review-mode row). The full
   *  label is still available via the icon's tooltip. */
  compact?: boolean;
}

export function LineItemStatusBadge({ status, compact = false }: LineItemStatusBadgeProps) {
  const c = config[status];
  const Icon = c.icon;
  if (compact) {
    // Bare colored icon — no filled badge background. Much cleaner against
    // the cells row in review mode where the green badge fill read as heavy.
    return (
      <span title={c.label} className="inline-flex shrink-0 items-center">
        <Icon className={cn('h-3.5 w-3.5', c.iconColor)} aria-label={c.label} />
      </span>
    );
  }
  return (
    <Badge variant={c.variant} className="gap-1 text-xs cursor-default">
      <Icon className="h-3 w-3" />
      {c.label}
    </Badge>
  );
}
